import { asbError } from '@project/common/util/log';
import { useCallback, useEffect, useState } from 'react';
import type { FileWithId } from '@project/common/file-selector';
import type { FileSessionRecord, FileSystemFileHandleWithId } from '@project/common/file-system-access';
import { IndexedDBFileSessionRepository, supportsFileSystemAccess } from '@project/common/file-system-access';

let _repository: IndexedDBFileSessionRepository | undefined;
const getRepository = () => {
    if (_repository === undefined && supportsFileSystemAccess()) {
        _repository = new IndexedDBFileSessionRepository();
    }
    return _repository;
};

export const useFileSession = () => {
    const fileSessionRepository = getRepository();
    const [canRestoreLastSession, setCanRestoreLastSession] = useState<boolean>(false);

    useEffect(() => {
        if (!fileSessionRepository) return;
        void fileSessionRepository
            .fetch()
            .then((record) => {
                if (
                    record &&
                    (record.videoHandle ||
                        record.subtitleHandles.length > 0 ||
                        (record.cachedSubtitleFiles?.length ?? 0) > 0)
                ) {
                    setCanRestoreLastSession(true);
                }
            })
            .catch((error) => {
                asbError('app/session', 'Failed to inspect the last file session:', error);
            });
    }, [fileSessionRepository]);

    const saveSession = useCallback(
        async ({ videoHandle, subtitleHandles }: Pick<FileSessionRecord, 'videoHandle' | 'subtitleHandles'>) => {
            if (!fileSessionRepository) return;

            if (!videoHandle && subtitleHandles.length === 0) {
                return;
            }

            await fileSessionRepository.merge({ videoHandle, subtitleHandles });
            setCanRestoreLastSession(true);
        },
        [fileSessionRepository]
    );

    const fetchSession = useCallback(() => fileSessionRepository?.fetch(), [fileSessionRepository]);

    const clearSession = useCallback(async () => {
        await fileSessionRepository?.clear();
        setCanRestoreLastSession(false);
    }, [fileSessionRepository]);

    const saveBufferedHandlesToSession = useCallback(
        async (bufferedSubtitleHandles: FileSystemFileHandleWithId[]) => {
            await fileSessionRepository?.merge({
                subtitleHandles: [],
                bufferedSubtitleHandles: bufferedSubtitleHandles,
            });
        },
        [fileSessionRepository]
    );
    const promoteBufferedHandlesInSession = useCallback(
        async (ids: string[]) => {
            await fileSessionRepository?.promoteBuffered(ids);
        },
        [fileSessionRepository]
    );

    const clearBufferedHandlesInSession = useCallback(async () => {
        await fileSessionRepository?.clearBuffered();
    }, [fileSessionRepository]);

    const saveCachedSubtitleFilesToSession = useCallback(
        async (files: FileWithId[]) => {
            const didSave = await fileSessionRepository?.setCachedSubtitleFiles(files);
            if (files.length > 0 && didSave) {
                setCanRestoreLastSession(true);
            }
        },
        [fileSessionRepository]
    );

    useEffect(() => {
        void fileSessionRepository?.clearBuffered();
    }, [fileSessionRepository]);

    const retainSourcesInSession = useCallback(
        async (ids: string[]) => {
            await fileSessionRepository?.retain(ids);
        },
        [fileSessionRepository]
    );

    return {
        canRestoreLastSession,
        saveSession,
        fetchSession,
        clearSession,
        saveBufferedHandlesToSession,
        promoteBufferedHandlesInSession,
        clearBufferedHandlesInSession,
        saveCachedSubtitleFilesToSession,
        retainSourcesInSession,
    };
};
